import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { ControleIntegracaoFiltro, ControleIntegracaoItem } from '../../core/models/integracoes/controle-integracao';
import { IntegracaoService } from '../../core/services/integracoes/integracao.service';

interface ResumoIntegracao {
  nome: string;
  execucoes: number;
  sucessos: number;
  erros: number;
  tempoMedio: number;
  percentual: number;
}

@Component({
  selector: 'app-controle-integracoes',
  templateUrl: './controle-integracoes.component.html',
  styleUrls: ['./controle-integracoes.component.css'],
  standalone: false
})
export class ControleIntegracoesComponent implements OnInit {
  readonly guidPermitido = 'A65C9C67-A1F9-470E-9534-17FB0E6CC516';
  filtro: ControleIntegracaoFiltro = {};
  registros: ControleIntegracaoItem[] = [];
  resumoIntegracoes: ResumoIntegracao[] = [];
  carregando = false;
  logSelecionado: ControleIntegracaoItem = null;

  totalExecucoes = 0;
  totalSucessos = 0;
  totalErros = 0;
  totalItens = 0;
  totalChamadas = 0;
  taxaSucesso = 0;
  tempoMedio = 0;

  constructor(
    private integracaoService: IntegracaoService,
    private router: Router,
    private toastr: ToastrService
  ) { }

  ngOnInit(): void {
    if (!this.podeAcessar()) {
      this.toastr.warning('Seu usuário não possui acesso a este painel.', 'Acesso restrito');
      this.router.navigate(['/pedidos']);
      return;
    }

    const hoje = this.formatarDataInput(new Date());
    this.filtro.data_inicio = hoje;
    this.filtro.data_fim = hoje;
    this.filtrar();
  }

  podeAcessar(): boolean {
    const guid = (localStorage.getItem('guidclientenapis') || '').toUpperCase();
    const gerenciaPermissoes = localStorage.getItem('flag_gerencia_permissoes') === 'true'
      || localStorage.getItem('flag_gerencia_permissoes') === '1';
    const menuIntegracoes = localStorage.getItem('flag_menu_integracoes');
    return guid === this.guidPermitido && gerenciaPermissoes
      && (menuIntegracoes == null || ['A', 'T'].includes(menuIntegracoes));
  }

  filtrar(): void {
    this.carregando = true;
    this.integracaoService.getControleIntegracoes(this.filtro).subscribe(registros => {
      this.registros = registros || [];
      this.calcularResumo();
      this.carregando = false;
    });
  }

  limpar(): void {
    const hoje = this.formatarDataInput(new Date());
    this.filtro = { data_inicio: hoje, data_fim: hoje };
    this.filtrar();
  }

  abrirLog(item: ControleIntegracaoItem): void {
    this.logSelecionado = item;
  }

  fecharLog(): void {
    this.logSelecionado = null;
  }

  formatarDataHora(valor: string): string {
    if (!valor) return '-';
    return new Date(valor).toLocaleString('pt-BR');
  }

  formatarDuracao(ms?: number): string {
    if (ms === null || ms === undefined || ms < 0) return '-';
    if (ms < 1000) return Math.round(ms) + ' ms';
    if (ms < 60000) return (ms / 1000).toFixed(ms < 10000 ? 1 : 0).replace('.', ',') + ' s';
    const minutos = Math.floor(ms / 60000);
    const segundos = Math.round((ms % 60000) / 1000);
    return minutos + ' min ' + segundos + ' s';
  }

  formatarLog(log: string): string {
    if (!log) return 'Nenhum log informado.';
    try {
      return JSON.stringify(JSON.parse(log), null, 2);
    } catch {
      return log;
    }
  }

  private calcularResumo(): void {
    this.totalExecucoes = this.registros.length;
    this.totalSucessos = this.registros.filter(x => x.sucesso).length;
    this.totalErros = this.totalExecucoes - this.totalSucessos;
    this.totalItens = this.registros.reduce((total, x) => total + (x.totalitens || 0), 0);
    this.totalChamadas = this.registros.reduce((total, x) => total + (x.chamadas_servicelayer || 0), 0);
    this.taxaSucesso = this.totalExecucoes ? (this.totalSucessos * 100) / this.totalExecucoes : 0;
    const duracoes = this.registros.filter(x => x.tempo_execucao_ms !== null && x.tempo_execucao_ms !== undefined);
    this.tempoMedio = duracoes.length
      ? duracoes.reduce((total, x) => total + x.tempo_execucao_ms, 0) / duracoes.length
      : 0;

    const grupos: { [nome: string]: ControleIntegracaoItem[] } = {};
    this.registros.forEach(item => {
      const nome = item.chamada_nome || 'Sem nome';
      grupos[nome] = grupos[nome] || [];
      grupos[nome].push(item);
    });

    this.resumoIntegracoes = Object.keys(grupos).map(nome => {
      const itens = grupos[nome];
      const sucessos = itens.filter(x => x.sucesso).length;
      const comDuracao = itens.filter(x => x.tempo_execucao_ms !== null && x.tempo_execucao_ms !== undefined);
      return {
        nome,
        execucoes: itens.length,
        sucessos,
        erros: itens.length - sucessos,
        tempoMedio: comDuracao.length
          ? comDuracao.reduce((total, x) => total + x.tempo_execucao_ms, 0) / comDuracao.length
          : 0,
        percentual: itens.length ? (sucessos * 100) / itens.length : 0
      };
    }).sort((a, b) => b.execucoes - a.execucoes);
  }

  private formatarDataInput(data: Date): string {
    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const dia = String(data.getDate()).padStart(2, '0');
    return `${ano}-${mes}-${dia}`;
  }
}
